import {
  Directive, ElementRef, EventEmitter, HostBinding, Input, OnChanges,
  OnDestroy, Output, Renderer2, SimpleChanges,
} from '@angular/core';
import { AngularResizeElementDirection } from './resize-element.enum';
import { AngularResizeElementEvent, Position } from './resize-element-event.interface';

/**
 * Drag-to-resize handle. In-repo replacement for the `angular-resize-element`
 * package, which was View Engine only and could not survive the Angular 16
 * upgrade (ngcc was removed). Selector, inputs, outputs and the emitted event
 * shape are deliberately identical to the original so consuming components only
 * change their import path.
 */
@Directive({
    selector: '[resize], [resizeStart], [resizeEnd]',
    standalone: false
})
export class AngularResizeElementDirective implements OnChanges, OnDestroy {
  @Input() public targetElement!: HTMLElement | ElementRef;
  @Input() public direction!: AngularResizeElementDirection;
  @Input() public proportionalResize = false;
  @Input() public rect!: Position;
  @Input() public applyClass = 'resizing';

  @Input()
  @HostBinding('attr.draggable')
  public useDrag: any;

  @Output() public readonly resizeStart = new EventEmitter<AngularResizeElementEvent>();
  @Output() public readonly resize = new EventEmitter<AngularResizeElementEvent>();
  @Output() public readonly resizeEnd = new EventEmitter<AngularResizeElementEvent>();

  private mouseClickListener?: () => void;
  private mouseUpListener?: () => void;
  private mouseMoveListener?: () => void;

  private targetElementWidthValue = 0;
  private targetElementHeightValue = 0;
  private targetElementTopValue = 0;
  private targetElementLeftValue = 0;
  private originalEvent!: MouseEvent;

  constructor(private readonly elementRef: ElementRef, private readonly renderer2: Renderer2) {
    this.listenMouseDownEvent();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['useDrag']) {
      this.listenMouseDownEvent();
    }
  }

  public ngOnDestroy(): void {
    this.detachMoveListeners();
    if (this.mouseClickListener) {
      this.mouseClickListener();
      this.mouseClickListener = undefined;
    }
  }

  private listenMouseDownEvent(): void {
    if (this.mouseClickListener) {
      this.mouseClickListener();
    }
    const event = this.useDrag ? 'dragstart' : 'mousedown';
    this.mouseClickListener = this.renderer2.listen(
      this.elementRef.nativeElement, event, (evt: MouseEvent) => this.onMouseDown(evt),
    );
  }

  private onMouseDown(evt: MouseEvent): void {
    evt.preventDefault();
    this.setOriginalData(evt);
    this.resizeStart.emit(this.generateValuesForEvent(evt));

    this.mouseUpListener = this.renderer2.listen('document', 'mouseup', (e: MouseEvent) => this.onMouseUp(e));
    this.mouseMoveListener = this.renderer2.listen('document', 'mousemove', (e: MouseEvent) => this.onMouseMove(e));
    this.renderer2.addClass(this.targetNativeElement, this.applyClass);
  }

  private onMouseUp(evt: MouseEvent): void {
    const eventValues = this.generateValuesForEvent(evt);
    this.resize.emit(eventValues);
    this.detachMoveListeners();
    this.renderer2.removeClass(this.targetNativeElement, this.applyClass);
    this.resizeEnd.emit(eventValues);
  }

  private onMouseMove(evt: MouseEvent): void {
    this.resize.emit(this.generateValuesForEvent(evt));
  }

  /** The two document-level listeners are always attached and removed together. */
  private detachMoveListeners(): void {
    if (this.mouseMoveListener) {
      this.mouseMoveListener();
      this.mouseMoveListener = undefined;
    }
    if (this.mouseUpListener) {
      this.mouseUpListener();
      this.mouseUpListener = undefined;
    }
  }

  private setOriginalData(originalEvent: MouseEvent): void {
    this.originalEvent = originalEvent;

    if (this.targetElement) {
      const dataSource = this.targetNativeElement;
      this.targetElementWidthValue = dataSource.offsetWidth;
      this.targetElementHeightValue = dataSource.offsetHeight;
      this.targetElementTopValue = dataSource.offsetTop;
      this.targetElementLeftValue = dataSource.offsetLeft;
    } else {
      this.targetElementWidthValue = 0;
      this.targetElementHeightValue = 0;
      this.targetElementTopValue = 0;
      this.targetElementLeftValue = 0;
    }
  }

  private get targetNativeElement(): HTMLElement {
    return this.targetElement instanceof ElementRef
      ? this.targetElement.nativeElement
      : this.targetElement;
  }

  private generateValuesForEvent(evt: MouseEvent): AngularResizeElementEvent {
    const originalXValue = this.originalEvent.clientX;
    const originalYValue = this.originalEvent.clientY;

    let diffWidthValue = evt.clientX - originalXValue;
    let diffHeightValue = evt.clientY - originalYValue;
    let diffTopValue = diffHeightValue;
    let diffLeftValue = diffWidthValue;

    switch (this.direction) {
      case AngularResizeElementDirection.TOP:
        diffHeightValue *= -1; diffWidthValue = 0; diffLeftValue = 0; break;
      case AngularResizeElementDirection.TOP_RIGHT:
        diffHeightValue *= -1; diffLeftValue = 0; break;
      case AngularResizeElementDirection.RIGHT:
        diffHeightValue = 0; diffTopValue = 0; diffLeftValue = 0; break;
      case AngularResizeElementDirection.BOTTOM_RIGHT:
        diffTopValue = 0; diffLeftValue = 0; break;
      case AngularResizeElementDirection.BOTTOM:
        diffWidthValue = 0; diffLeftValue = 0; diffTopValue = 0; break;
      case AngularResizeElementDirection.BOTTOM_LEFT:
        diffWidthValue *= -1; diffTopValue = 0; break;
      case AngularResizeElementDirection.LEFT:
        diffWidthValue *= -1; diffHeightValue = 0; diffTopValue = 0; break;
      case AngularResizeElementDirection.TOP_LEFT:
        diffHeightValue *= -1; diffWidthValue *= -1; break;
    }

    let currentWidthValue = this.targetElementWidthValue + diffWidthValue;
    let currentHeightValue = this.targetElementHeightValue + diffHeightValue;

    if (this.proportionalResize) {
      if (currentWidthValue > currentHeightValue) {
        currentWidthValue = currentHeightValue;
      } else {
        currentHeightValue = currentWidthValue;
      }
    }

    if (currentHeightValue <= 1) { diffTopValue += currentHeightValue; }
    if (currentWidthValue <= 1) { diffLeftValue += currentWidthValue; }
    if (currentWidthValue <= 0) { currentWidthValue = 0; }
    if (currentHeightValue <= 0) { currentHeightValue = 0; }

    let currentTopValue = this.targetElementTopValue + diffTopValue;
    let currentLeftValue = this.targetElementLeftValue + diffLeftValue;

    if (this.rect) {
      if (currentTopValue < this.rect.top) {
        currentHeightValue = this.targetElementHeightValue + this.targetElementTopValue - this.rect.top;
        currentTopValue = this.rect.top;
      }
      if (currentHeightValue + currentTopValue > this.rect.height) {
        currentHeightValue = this.rect.height - currentTopValue;
      }
      if (currentLeftValue < this.rect.left) {
        currentWidthValue = this.targetElementWidthValue + this.targetElementLeftValue - this.rect.left;
        currentLeftValue = this.rect.left;
      }
      if (currentWidthValue + currentLeftValue > this.rect.width) {
        currentWidthValue = this.rect.width - currentLeftValue;
      }
    }

    return {
      originalEvent: this.originalEvent,
      currentWidthValue,
      currentHeightValue,
      currentTopValue,
      currentLeftValue,
      originalWidthValue: this.targetElementWidthValue,
      originalHeightValue: this.targetElementHeightValue,
      originalTopValue: this.targetElementTopValue,
      originalLeftValue: this.targetElementLeftValue,
      differenceWidthValue: currentWidthValue - this.targetElementWidthValue,
      differenceHeightValue: currentHeightValue - this.targetElementHeightValue,
      differenceTopValue: currentTopValue - this.targetElementTopValue,
      differenceLeftValue: currentLeftValue - this.targetElementLeftValue,
      direction: this.direction,
    };
  }
}
